import React, { useState, useEffect, useCallback } from 'react'
import { errorMessage, unwrap } from 'api/call'
import {
  getFitPlayerProfilesSearchForMergeByQ,
  type PlayerMergeSearch,
} from 'api/generated'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { toast } from 'sonner'

interface SearchablePlayerProps {
  label: string
  player: PlayerMergeSearch | null
  onChangePlayerHandler: (player: PlayerMergeSearch | null) => void
}

const SearchablePlayer: React.FC<SearchablePlayerProps> = ({
  label,
  player,
  onChangePlayerHandler,
}) => {
  const [input, setInput] = useState('')
  const [playerResults, setPlayerResults] = useState<
    PlayerMergeSearch[] | null
  >(null)

  const fetchPlayerResults = useCallback(() => {
    if (input.length === 0) {
      setPlayerResults(null)
      return
    }

    unwrap(getFitPlayerProfilesSearchForMergeByQ({ path: { q: input } }))
      .then(setPlayerResults)
      .catch((error) => toast.error(errorMessage(error, 'Ricerca fallita')))
  }, [input])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchPlayerResults()
  }, [fetchPlayerResults])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
  }

  const handlePlayerClick = (selectedPlayer: PlayerMergeSearch) => {
    setInput('')
    setPlayerResults(null)
    onChangePlayerHandler(selectedPlayer)
  }

  const handleRemovePlayer = () => {
    onChangePlayerHandler(null)
  }

  const renderPlayersResults = () => {
    if (!playerResults || input.length === 0) return null

    return (
      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-border'>
          <thead className='bg-muted'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-muted-foreground uppercase'>
                ID
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-muted-foreground uppercase'>
                Nome
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-muted-foreground uppercase'>
                Classifica
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-muted-foreground uppercase'>
                Tessera
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-muted-foreground uppercase'>
                Codice Anagrafica
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-border bg-background'>
            {playerResults.map((playerResult) => (
              <tr
                key={playerResult.id}
                onClick={() => handlePlayerClick(playerResult)}
                className='cursor-pointer hover:bg-muted/50'
              >
                <td className='px-6 py-4 whitespace-nowrap'>
                  {playerResult.id}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {playerResult.name}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {playerResult.rank}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {playerResult.card_number}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {playerResult.anagrafica_code}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderNoResults = () => {
    return (
      <p className='text-muted-foreground'>
        Nessun giocatore corrispondente al criterio di ricerca
      </p>
    )
  }

  const renderResults = () => {
    if (!playerResults) return null

    return (
      <div className='mt-2'>
        {playerResults.length === 0 && input.length !== 0
          ? renderNoResults()
          : renderPlayersResults()}
      </div>
    )
  }

  const renderPlayer = () => {
    if (!player) return null

    return (
      <div className='mt-2 rounded border p-4'>
        <p className='mb-2'>
          <strong className='mr-2'>Giocatore selezionato</strong>
          <Button onClick={handleRemovePlayer} size='sm'>
            Togli
          </Button>
        </p>
        <p className='mb-1'>
          <strong>ID:</strong> {player.id}
        </p>
        <p className='mb-1'>
          <strong>Nome:</strong> {player.name}
        </p>
        <p className='mb-1'>
          <strong>Classifica:</strong> {player.rank}
        </p>
        <p className='mb-1'>
          <strong>Tessera:</strong> {player.card_number}
        </p>
        <p className='mb-1'>
          <strong>Codice Angrafica:</strong> {player.anagrafica_code}
        </p>
      </div>
    )
  }

  const renderInputArea = () => {
    return (
      <div>
        <Input
          type='text'
          className='h-8 w-37.5 lg:w-62.5'
          value={input}
          onChange={handleInputChange}
        />
        {renderResults()}
      </div>
    )
  }

  return (
    <div className='mb-4'>
      <label className='block text-sm font-medium'>{label}</label>
      {player ? renderPlayer() : renderInputArea()}
    </div>
  )
}

export default SearchablePlayer
